package org.example.project.ui.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.example.project.client.model.ActivityStatus
import org.example.project.ui.icons.AppIcons
import org.example.project.ui.theme.LocalPlayfulShapes
import org.example.project.ui.theme.PlayfulColors

@Composable
fun CategoryBadge(
    category: String,
    modifier: Modifier = Modifier
) {
    val shapes = LocalPlayfulShapes.current
    Surface(
        color = PlayfulColors.TealLight,
        shape = shapes.pill,
        border = BorderStroke(1.dp, PlayfulColors.TealBorder),
        modifier = modifier
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp)
        ) {
            Text(
                text = category,
                color = PlayfulColors.TealDark,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold
            )
        }
    }
}

@Composable
fun AgeBadge(
    minAge: Int,
    maxAge: Int,
    modifier: Modifier = Modifier
) {
    val shapes = LocalPlayfulShapes.current
    Surface(
        color = PlayfulColors.LilacLight,
        shape = shapes.pill,
        border = BorderStroke(1.dp, PlayfulColors.LilacBorder),
        modifier = modifier
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.padding(horizontal = 9.dp, vertical = 4.dp)
        ) {
            Icon(
                imageVector = AppIcons.Users,
                contentDescription = null,
                tint = PlayfulColors.LilacDark,
                modifier = Modifier.size(12.dp)
            )
            Spacer(modifier = Modifier.width(4.dp))
            Text(
                text = "$minAge–$maxAge lat",
                color = PlayfulColors.LilacDark,
                fontSize = 11.sp,
                fontWeight = FontWeight.Medium
            )
        }
    }
}

@Composable
fun DurationBadge(
    durationMinutes: Int,
    modifier: Modifier = Modifier
) {
    val shapes = LocalPlayfulShapes.current
    Surface(
        color = PlayfulColors.SunYellowLight,
        shape = shapes.pill,
        border = BorderStroke(1.dp, PlayfulColors.SunYellowBorder),
        modifier = modifier
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.padding(horizontal = 9.dp, vertical = 4.dp)
        ) {
            Icon(
                imageVector = AppIcons.Clock,
                contentDescription = null,
                tint = PlayfulColors.SunYellowDark,
                modifier = Modifier.size(12.dp)
            )
            Spacer(modifier = Modifier.width(4.dp))
            Text(
                text = "$durationMinutes min",
                color = PlayfulColors.SunYellowDark,
                fontSize = 11.sp,
                fontWeight = FontWeight.Medium
            )
        }
    }
}

@Composable
fun ActivityStatusBadge(
    status: ActivityStatus,
    modifier: Modifier = Modifier
) {
    val shapes = LocalPlayfulShapes.current
    val isDraft = status == ActivityStatus.DRAFT
    val (bg, fg, border, label) = if (isDraft) {
        listOf(
            PlayfulColors.SunYellowLight,
            PlayfulColors.SunYellowDark,
            PlayfulColors.SunYellowBorder,
            "SZKIC"
        )
    } else {
        listOf(
            PlayfulColors.SuccessContainer,
            PlayfulColors.OnSuccessContainer,
            PlayfulColors.SuccessBorder,
            "OPUBLIKOWANA"
        )
    }

    Surface(
        color = bg as Color,
        shape = shapes.pill,
        border = BorderStroke(1.dp, border as Color),
        modifier = modifier
    ) {
        Text(
            text = label as String,
            color = fg as Color,
            fontSize = 10.sp,
            fontWeight = FontWeight.Bold,
            letterSpacing = 0.5.sp,
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 3.dp)
        )
    }
}

@Composable
fun FavoriteToggleButton(
    isFavorite: Boolean,
    onToggle: () -> Unit,
    modifier: Modifier = Modifier
) {
    val shapes = LocalPlayfulShapes.current
    Surface(
        color = if (isFavorite) PlayfulColors.SunYellowLight else PlayfulColors.SurfaceVariant,
        shape = shapes.pill,
        border = BorderStroke(
            1.dp,
            if (isFavorite) PlayfulColors.SunYellowBorder else PlayfulColors.Border
        ),
        modifier = modifier.clickable { onToggle() }
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp)
        ) {
            Icon(
                imageVector = if (isFavorite) AppIcons.StarFilled else AppIcons.StarOutline,
                contentDescription = if (isFavorite) "Usuń z ulubionych" else "Dodaj do ulubionych",
                tint = if (isFavorite) PlayfulColors.SunYellowDark else PlayfulColors.TextSecondary,
                modifier = Modifier.size(16.dp)
            )
            Spacer(modifier = Modifier.width(6.dp))
            Text(
                text = if (isFavorite) "Ulubiona" else "Do ulubionych",
                fontSize = 12.sp,
                fontWeight = if (isFavorite) FontWeight.SemiBold else FontWeight.Medium,
                color = if (isFavorite) PlayfulColors.SunYellowDark else PlayfulColors.TextSecondary
            )
        }
    }
}
